import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import type { McpProtocolClient } from '@/lib/mcp/protocolClient';
import { toUserMessage } from '@/lib/api/errors';
import type {
  McpResource,
  McpResourceTemplate,
  McpPrompt,
  McpPromptMessage,
  McpTask,
  BuildStatus,
  InvocationLog,
} from '@/lib/mcp/types';

export const useMcpProtocolStore = defineStore('mcpProtocol', () => {
  // State
  const resources = ref<McpResource[]>([]);
  const resourceTemplates = ref<McpResourceTemplate[]>([]);
  const prompts = ref<McpPrompt[]>([]);
  const tasks = ref<McpTask[]>([]);
  const builds = ref<BuildStatus[]>([]);
  const invocationLogs = ref<InvocationLog[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const runningTasks = computed(() =>
    tasks.value.filter((t) => t.status === 'working'),
  );

  const completedTasks = computed(() =>
    tasks.value.filter((t) => t.status === 'completed'),
  );

  const failedBuilds = computed(() =>
    builds.value.filter((b) => b.state === 'failed'),
  );

  // Actions
  const fetchResources = async (client: McpProtocolClient): Promise<void> => {
    isLoading.value = true;
    error.value = null;
    try {
      const [res, templates] = await Promise.all([
        client.listResources(),
        client.listResourceTemplates(),
      ]);
      resources.value = res;
      resourceTemplates.value = templates;
    } catch (e) {
      error.value = toUserMessage(e, 'Failed to load resources');
    } finally {
      isLoading.value = false;
    }
  };

  const readResource = async (
    client: McpProtocolClient,
    uri: string,
  ): Promise<{ contents: Array<{ uri: string; text?: string; blob?: string; mimeType?: string }> }> => {
    return client.readResource(uri);
  };

  const fetchPrompts = async (client: McpProtocolClient): Promise<void> => {
    isLoading.value = true;
    error.value = null;
    try {
      prompts.value = await client.listPrompts();
    } catch (e) {
      error.value = toUserMessage(e, 'Failed to load prompts');
    } finally {
      isLoading.value = false;
    }
  };

  const getPrompt = async (
    client: McpProtocolClient,
    name: string,
    args?: Record<string, string>,
  ): Promise<{ messages: McpPromptMessage[] }> => {
    return client.getPrompt(name, args);
  };

  return {
    // State
    resources,
    resourceTemplates,
    prompts,
    tasks,
    builds,
    invocationLogs,
    isLoading,
    error,
    // Getters
    runningTasks,
    completedTasks,
    failedBuilds,
    // Actions
    fetchResources,
    readResource,
    fetchPrompts,
    getPrompt,
  };
});
